/**
 * Centralized Real Media Configuration for Rahber Corporation
 * High-definition authentic photos and videos of the flagship showroom in Lahore,
 * verified team members, genuine brand product displays, and customer floor activities.
 */

// ── 1. Shop & Showroom Architecture ──────────────────────────────────────────
export const REAL_SHOP_IMAGES = {
    // Flagship storefront with red Dawlance / Rahber Corporation banner & Urdu branding
    storefront: '/assets/media/images/shop/showroom.jpg',
    // Window display featuring Multynet QLED TVs, blenders, fans, and brand signage
    windowDisplay: '/assets/media/images/shop/IMG_5699.HEIC_20261022225000.jpg',
    // Main showroom aisle with Orient, Dawlance and PEL Inverter Refrigerators
    refrigeratorAisle: '/assets/media/images/shop/IMG_4532.HEIC_20261022225000.jpg',
    // Spacious aisle with PEL Jumbo Deep Freezers and ambient blue floor LED accents
    freezerAisle: '/assets/media/images/shop/IMG_4533.HEIC_20261022225000.jpg',
    // Deep freezer floor display with official installment pricing and brand tags
    deepFreezerFloor: '/assets/media/images/shop/IMG_5606.HEIC_20261022225000.jpg',
};

// ── 2. Real Team & Leadership ────────────────────────────────────────────────
export const REAL_TEAM_MEMBERS = [
    {
        id: 'naeem-shafi',
        name: 'Mr. Naeem Shafi',
        role: 'Founder & Managing Director',
        experience: '45+ Years Leadership',
        image: '/assets/media/images/team/owner.jpeg',
        bio: 'Established Rahber Corporation in 1980 in Lahore with an enduring commitment to honest dealing, fair rates, and genuine customer care.',
        badge: 'Executive Leadership',
    },
    {
        id: 'ceo',
        name: 'Chief Executive Officer',
        role: 'Chief Executive Officer (CEO)',
        experience: 'Executive Leadership',
        image: '/assets/media/images/team/ceo.jpg', // Replace with your CEO image URL / path
        bio: 'Driving strategic growth, modern retail innovation, and customer-centric leadership for Rahber Corporation.',
        badge: 'Executive Leadership',
    },
    {
        id: 'sales-manager',
        name: 'Muhammad Tariq',
        role: 'Senior Sales & Operations Manager',
        experience: '18+ Years at Rahber',
        image: '/assets/media/images/team/IMG_5711.HEIC_20261022224927.jpg',
        bio: 'Oversees daily showroom floor operations, installment approvals, and official corporate partner relations across Punjab.',
        badge: 'Showroom Management',
    },
    {
        id: 'customer-relations',
        name: 'Bilal Ahmad',
        role: 'Customer Care & Installment Advisor',
        experience: '8+ Years Experience',
        image: '/assets/media/images/team/IMG_5703.HEIC_20261022224927.jpg',
        bio: 'Guides walk-in customers through 0% markup 4-month and extended installment plans with fast same-day verification.',
        badge: 'Customer Consultation',
    },
    {
        id: 'cooling-advisor',
        name: 'Usman Ali',
        role: 'Inverter AC & Cooling Specialist',
        experience: '10+ Years Experience',
        image: '/assets/media/images/team/IMG_5695.HEIC_20261022224927.jpg',
        bio: 'Technical adviser specializing in room BTU calculations, T3 tropical compressor specs, and inverter energy savings.',
        badge: 'Cooling Advisor',
    },
    {
        id: 'appliance-consultant',
        name: 'Hamza Shafi',
        role: 'Smart LED TV & Home Appliance Consultant',
        experience: '6+ Years Experience',
        image: '/assets/media/images/team/IMG_5693.HEIC_20261022224927.jpg',
        bio: 'Demonstrates Google TV features, QLED panel capabilities, and smart kitchen automation for visiting families.',
        badge: 'Electronics Expert',
    },
    {
        id: 'dispatch-coordinator',
        name: 'Ali Raza',
        role: 'Warehouse & Doorstep Delivery Lead',
        experience: '7+ Years Experience',
        image: '/assets/media/images/team/IMG_5696.HEIC_20261022224927.jpg',
        bio: 'Coordinates safe, box-packed doorstep transport across Lahore with verified delivery receipts and unboxing checks.',
        badge: 'Logistics & Dispatch',
    },
];

// ── 3. Real Product Displays ─────────────────────────────────────────────────
export const REAL_PRODUCT_DISPLAYS = [
    {
        id: 'tv-wall',
        title: 'Smart LED TV Showcase Wall',
        category: 'LED TVs & Soundbars',
        brands: 'Samsung, TCL, Multynet, Sony, Changhong Ruba',
        image: '/assets/media/images/products/IMG_4508.HEIC_20261022224947.jpg',
        desc: 'Multi-brand 4K UHD smart display wall offering interactive side-by-side picture quality comparisons.',
    },
    {
        id: 'refrigerators-line',
        title: 'Modern Inverter Refrigerators Line',
        category: 'Refrigerators',
        brands: 'Dawlance, PEL, Orient, Haier',
        image: '/assets/media/images/products/IMG_4528.HEIC_20261022224947.jpg',
        desc: 'Wide selection of glass-door, inverter and low-voltage start refrigerators with 10-year official compressor warranties.',
    },
    {
        id: 'sound-systems',
        title: 'Entertainment & Audio Systems',
        category: 'Audio & Home Theaters',
        brands: 'F&D, Audionic, Changhong Ruba, Sony',
        image: '/assets/media/images/products/IMG_5677.HEIC_20261022224947.jpg',
        desc: 'Surround soundbars, wireless subwoofers, and trolley party speakers ready for live acoustic demonstration.',
    },
    {
        id: 'deep-freezers',
        title: 'Jumbo Deep Freezers Section',
        category: 'Deep Freezers',
        brands: 'Waves, PEL, Haier',
        image: '/assets/media/images/shop/IMG_5606.HEIC_20261022225000.jpg',
        desc: 'Heavy-duty single and double door chest freezers with quick freeze technology, available on 0% markup installments.',
    },
    {
        id: 'window-appliances',
        title: 'Premium Display Window',
        category: 'Small Appliances & Gadgets',
        brands: 'Multynet, Dawlance, Philips, Westpoint',
        image: '/assets/media/images/shop/IMG_5699.HEIC_20261022225000.jpg',
        desc: 'Storefront showcases featuring food processors, microwave ovens, blenders, and pedestal cooling fans.',
    },
    {
        id: 'refrigerator-aisle',
        title: 'Central Showroom Appliance Aisle',
        category: 'Showroom Interior',
        brands: 'Orient, PEL, Dawlance',
        image: '/assets/media/images/shop/IMG_4532.HEIC_20261022225000.jpg',
        desc: 'Spacious wide-aisle customer experience allowing easy side-by-side inspection of authentic box-packed stock.',
    },
];

// ── 4. Real Showroom & Activity Videos (Fetched dynamically via backend APIs) ──
export const REAL_SHOWROOM_VIDEOS = [];

// ── 5. Curated Gallery Items for About & Showroom Pages ──────────────────────
export const REAL_GALLERY_ITEMS = [
    {
        src: REAL_SHOP_IMAGES.storefront,
        label: 'Flagship Showroom Exterior',
        desc: 'Main Bazar Chandni Chowk, Sanda Kalan, Lahore — Welcoming customers since 1980',
        tag: 'Storefront',
    },
    {
        src: REAL_SHOP_IMAGES.refrigeratorAisle,
        label: 'Refrigerators & Cooling Gallery',
        desc: 'Full lineup of Dawlance, PEL, Orient, and Haier Inverter cooling solutions',
        tag: 'Cooling',
    },
    {
        src: '/assets/media/images/products/IMG_4508.HEIC_20261022224947.jpg',
        label: '4K Smart LED TV Wall',
        desc: 'Samsung, TCL, Multynet, and Changhong Ruba displays ready for side-by-side demo',
        tag: 'LED TVs',
    },
    {
        src: REAL_SHOP_IMAGES.freezerAisle,
        label: 'Jumbo Deep Freezers Aisle',
        desc: 'High-capacity commercial and household chest freezers with ambient LED showroom lighting',
        tag: 'Freezers',
    },
    {
        src: REAL_SHOP_IMAGES.deepFreezerFloor,
        label: 'Installment Display Floor',
        desc: 'Transparent pricing with zero hidden fees — instant 4 to 12 months installment plans',
        tag: 'Installments',
    },
    {
        src: REAL_SHOP_IMAGES.windowDisplay,
        label: 'Main Street Display Windows',
        desc: 'Showcasing Multynet QLED TVs, high-velocity pedestal fans, and premium kitchen appliances',
        tag: 'Display',
    },
];
